import React, { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import api from '../api/axios'

export default function CheckoutPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const planId = searchParams.get('planId')

  const [plans, setPlans] = useState<any[]>([])
  const [selectedPlan, setSelectedPlan] = useState<any>(null)
  const [includeAddon, setIncludeAddon] = useState(false)
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', companyName: '',
    country: '', state: '', city: '', zip: '', streetAddress: ''
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [step, setStep] = useState<'details' | 'confirm'>('details')
  const [couponCode, setCouponCode] = useState('')
  const [couponResult, setCouponResult] = useState<any>(null)
  const [couponError, setCouponError] = useState('')
  const [applyingCoupon, setApplyingCoupon] = useState(false)

  const labelStyle: React.CSSProperties = { color: '#374151', fontSize: 11, fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: 1, display: 'block', marginBottom: 6 }
  const inputStyle: React.CSSProperties = { width: '100%', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 14px', color: '#111827', fontSize: 14, outline: 'none', boxSizing: 'border-box' as const }

  useEffect(() => {
    api.get('/payment/plans').then(res => {
      setPlans(res.data)
      const plan = planId ? res.data.find((p: any) => p.planId === planId) : res.data[0]
      setSelectedPlan(plan || res.data[0])
    }).catch(() => {})
  }, [planId])

  // Reset addon toggle when plan changes
  useEffect(() => {
    setIncludeAddon(false)
  }, [selectedPlan?.planId])

  const handleProceed = () => {
    if (!form.firstName.trim()) { setError('Please enter your first name'); return }
    if (!form.lastName.trim()) { setError('Please enter your last name'); return }
    if (!form.email.trim() || !form.email.includes('@')) { setError('Please enter a valid email'); return }
    if (!form.companyName.trim()) { setError('Please enter your company name'); return }
    if (!form.country.trim()) { setError('Please select your country'); return }
    if (!form.city.trim()) { setError('Please enter your city'); return }
    if (!form.zip.trim()) { setError('Please enter your ZIP code'); return }
    setError('')
    setStep('confirm')
  }

  const handlePayPal = async () => {
    if (!selectedPlan?.price || Number(selectedPlan.price) <= 0) {
      setError('This plan is not available for online payment. Please contact us.')
      return
    }
    setLoading(true)
    setError('')
    try {
      const res = await api.post('/payment/create-order', {
        planId: selectedPlan.planId,
        isNewClient: true,
        includeAddon,
        name: `${form.firstName} ${form.lastName}`,
        email: form.email,
        phone: null,
        address: `${form.streetAddress ? form.streetAddress + ', ' : ''}${form.city}, ${form.state ? form.state + ', ' : ''}${form.country} ${form.zip}`.trim(),
        companyName: form.companyName,
        firstName: form.firstName,
        lastName: form.lastName,
        city: form.city,
        state: form.state,
        country: form.country,
        zip: form.zip,
        streetAddress: form.streetAddress,
        couponCode: couponResult ? couponCode : undefined
      })
      window.location.href = res.data.approvalUrl
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to initiate payment. Please try again.')
      setLoading(false)
    }
  }

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return
    setApplyingCoupon(true)
    setCouponError('')
    setCouponResult(null)
    try {
      const res = await api.post('/coupons/validate', {
        code: couponCode,
        planId: selectedPlan?.planId,
        email: form.email
      })
      setCouponResult(res.data)
    } catch (e: any) {
      setCouponError(e.response?.data?.error || 'Invalid coupon code')
    }
    setApplyingCoupon(false)
    }

  const addonAvailable = selectedPlan?.addonPrice && Number(selectedPlan.addonPrice) > 0
  const total = selectedPlan
  ? (() => {
      let t = Number(selectedPlan.price) + (includeAddon && addonAvailable ? Number(selectedPlan.addonPrice) : 0)
      if (couponResult) {
        if (couponResult.discountType === 'percentage') t = t - (t * couponResult.discountValue / 100)
        else t = Math.max(0, t - couponResult.discountValue)
      }
      return t.toFixed(2)
    })()
  : '0.00'

  return (
    <div style={{ minHeight: '100vh', background: '#f9f9f9', fontFamily: 'Poppins, sans-serif' }}>
      {/* Top bar */}
      <div style={{ background: '#FF7F32', padding: '10px 24px', textAlign: 'center' }}>
        <span style={{ color: '#fff', fontWeight: 600, fontSize: 13 }}>
          Start your subscription today and save $4000 each month!
        </span>
      </div>

      {/* Nav */}
      <nav style={{ background: '#111', padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <a href="https://subscribe.inkcrediblestudios.com" style={{ color: '#9CA3AF', fontSize: 13, textDecoration: 'none' }}>
          ← Back to website
        </a>
        <a href="https://subscribe.inkcrediblestudios.com">
        <img src="/New Inkcredible Logo.webp" alt="Inkcredible" style={{ height: 40, objectFit: 'contain', cursor: 'pointer' }} />
</a>
      </nav>

      {/* Main content */}
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 16px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 32, alignItems: 'start' }}>

        {/* Left — Plan summary */}
        <div>
          <h1 style={{ color: '#111827', fontSize: 28, fontWeight: 700, marginBottom: 8 }}>
            Complete Your Subscription
          </h1>
          <p style={{ color: '#6B7280', fontSize: 14, marginBottom: 32 }}>
            You're one step away from unlimited artwork services.
          </p>

          {/* Selected plan summary */}
          {selectedPlan && (
            <div style={{ marginBottom: 24, background: '#fff', border: '2px solid #FF7F32', borderRadius: 16, padding: '20px 24px' }}>
              <p style={{ color: '#FF7F32', fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 12 }}>
                Your Selected Plan
              </p>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <div>
                  <p style={{ color: '#111827', fontWeight: 700, fontSize: 18, margin: 0 }}>{selectedPlan.planName}</p>
                  <p style={{ color: '#6B7280', fontSize: 13, marginTop: 4 }}>{selectedPlan.jobCount} jobs · {selectedPlan.periodDays} days</p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <p style={{ color: '#FF7F32', fontWeight: 800, fontSize: 28, margin: 0 }}>${selectedPlan.price}</p>
                  <p style={{ color: '#9CA3AF', fontSize: 12, marginTop: 2 }}>per {selectedPlan.periodDays <= 7 ? 'week' : 'month'}</p>
                {includeAddon && addonAvailable && (
                  <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #ffe0c8' }}>
                    <p style={{ color: '#9CA3AF', fontSize: 11, margin: 0 }}>Total</p>
                    <p style={{ color: '#FF7F32', fontWeight: 800, fontSize: 24, margin: '2px 0 0' }}>
                      ${(Number(selectedPlan.price) + Number(selectedPlan.addonPrice)).toFixed(2)}
                      <span style={{ color: '#9CA3AF', fontSize: 12, fontWeight: 400 }}>/{selectedPlan.periodDays <= 7 ? 'wk' : 'mo'}</span>
                    </p>
                  </div>
                )}
                </div>
              </div>
              {selectedPlan.description && (
                <p style={{ color: '#6B7280', fontSize: 13, marginBottom: 0, borderTop: '1px solid #ffe0c8', paddingTop: 12 }}>
                  {selectedPlan.description}
                </p>
              )}
            </div>
          )}

          {/* Addon toggle button */}
{addonAvailable && (
  <div
    onClick={() => setIncludeAddon(v => !v)}
    style={{
      marginBottom: 16, padding: '14px 18px', borderRadius: 12, cursor: 'pointer',
      border: `2px solid ${includeAddon ? '#FF7F32' : '#e5e7eb'}`,
      background: includeAddon ? '#fff4ee' : '#fafafa',
      display: 'flex', alignItems: 'center', gap: 12, transition: 'all 0.2s'
    }}>
    {/* Checkbox */}
    <div style={{
      width: 20, height: 20, borderRadius: 6, flexShrink: 0,
      border: `2px solid ${includeAddon ? '#FF7F32' : '#d1d5db'}`,
      background: includeAddon ? '#FF7F32' : '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      transition: 'all 0.2s'
    }}>
      {includeAddon && <span style={{ color: '#fff', fontSize: 12, fontWeight: 700 }}>✓</span>}
    </div>
    <div style={{ flex: 1 }}>
      <p style={{ color: '#111827', fontWeight: 700, fontSize: 13, margin: 0 }}>
        ⚡ Add Unlimited Revisions
      </p>
      <p style={{ color: '#6B7280', fontSize: 11, marginTop: 2 }}>
        Unlock unlimited revisions for the entire period
      </p>
    </div>
    <p style={{ color: '#FF7F32', fontWeight: 800, fontSize: 16, margin: 0, flexShrink: 0 }}>
      +${Number(selectedPlan?.addonPrice).toFixed(0)}
    </p>
  </div>
)}

          {/* What's included */}
          {selectedPlan && (
            <div style={{ background: '#fff4ee', borderRadius: 12, padding: '20px 24px', border: '1px solid #ffe0c8' }}>
              <p style={{ color: '#FF7F32', fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 12 }}>
                What's Included
              </p>
              {[
                `${selectedPlan.jobCount} Jobs per period`,
                'Personal Artwork Portal Access',
                'Secure File Storage',
                selectedPlan.unlimitedRevisions ? 'Unlimited Revisions' : `${selectedPlan.freeRevisions} Free Revisions`,
                'Priority Delivery',
              ].map(feature => (
                <div key={feature} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <span style={{ color: '#22c55e', fontSize: 14 }}>✓</span>
                  <span style={{ color: '#374151', fontSize: 13 }}>{feature}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right — Form */}
        <div style={{ background: '#ffffff', borderRadius: 16, padding: 32, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', border: '1px solid #e5e7eb' }}>
          {step === 'details' ? (
            <>
              <h2 style={{ color: '#111827', fontSize: 20, fontWeight: 700, marginBottom: 24 }}>Your Details</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {/* First + Last Name */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
                  <div>
                    <label style={labelStyle}>First Name *</label>
                    <input style={inputStyle} placeholder="John" value={form.firstName} onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))} />
                  </div>
                  <div>
                    <label style={labelStyle}>Last Name *</label>
                    <input style={inputStyle} placeholder="Smith" value={form.lastName} onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))} />
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>Email Address *</label>
                  <input style={inputStyle} type="email" placeholder="john@company.com" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
                </div>
                <div>
                  <label style={labelStyle}>Company Name *</label>
                  <input style={inputStyle} placeholder="Acme Corp" value={form.companyName} onChange={e => setForm(f => ({ ...f, companyName: e.target.value }))} />
                </div>
                <div>
                  <label style={labelStyle}>Country *</label>
                  <select style={inputStyle} value={form.country} onChange={e => setForm(f => ({ ...f, country: e.target.value }))}>
                    <option value="">Select country...</option>
                    {['United States','United Kingdom','Canada','Australia','India','Germany','France','Netherlands','Singapore','UAE','South Africa','New Zealand','Ireland','Other'].map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
                  <div>
                    <label style={labelStyle}>City *</label>
                    <input style={inputStyle} placeholder="New York" value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} />
                  </div>
                  <div>
                    <label style={labelStyle}>State <span style={{ color: '#6B7280', fontWeight: 400 }}>(optional)</span></label>
                    <input style={inputStyle} placeholder="NY" value={form.state} onChange={e => setForm(f => ({ ...f, state: e.target.value }))} />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12 }}>
                  <div>
                    <label style={labelStyle}>ZIP Code *</label>
                    <input style={inputStyle} placeholder="10001" value={form.zip} onChange={e => setForm(f => ({ ...f, zip: e.target.value }))} />
                  </div>
                  <div>
                    <label style={labelStyle}>Street Address <span style={{ color: '#6B7280', fontWeight: 400 }}>(optional)</span></label>
                    <input style={inputStyle} placeholder="123 Main St" value={form.streetAddress} onChange={e => setForm(f => ({ ...f, streetAddress: e.target.value }))} />
                  </div>
                </div>
                {/* Coupon Code */}
                <div>
                  <label style={labelStyle}>Coupon Code <span style={{ color: '#9CA3AF', fontWeight: 400 }}>(optional)</span></label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input style={{ ...inputStyle, flex: 1 }} placeholder="Enter coupon code" value={couponCode} onChange={e => setCouponCode(e.target.value.toUpperCase())} />
                    <button onClick={handleApplyCoupon} disabled={applyingCoupon || !couponCode.trim()}
                      style={{ background: '#FF7F32', color: '#fff', fontWeight: 700, fontSize: 13, padding: '10px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', opacity: (!couponCode.trim() || applyingCoupon) ? 0.5 : 1, flexShrink: 0 }}>
                      {applyingCoupon ? '...' : 'Apply'}
                    </button>
                  </div>
                  {couponResult && (
                    <p style={{ color: '#22c55e', fontSize: 12, marginTop: 6 }}>
                      ✓ {couponResult.name} applied — {couponResult.discountType === 'percentage' ? `${couponResult.discountValue}% off` : `$${couponResult.discountValue} off`}
                    </p>
                  )}
                  {couponError && <p style={{ color: '#ef4444', fontSize: 12, marginTop: 6 }}>{couponError}</p>}
                </div>
                {error && (
                  <p style={{ color: '#f87171', fontSize: 13, background: 'rgba(239,68,68,0.1)', padding: '10px 14px', borderRadius: 8 }}>{error}</p>
                )}
                <button
                  onClick={handleProceed}
                  style={{ background: '#FF7F32', color: '#fff', fontWeight: 700, fontSize: 15, padding: '14px', borderRadius: 10, border: 'none', cursor: 'pointer', marginTop: 8 }}>
                  Continue to Payment →
                </button>
              </div>
            </>
          ) : (
            <>
              <h2 style={{ color: '#111827', fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Confirm & Pay</h2>
              <p style={{ color: '#6B7280', fontSize: 13, marginBottom: 24 }}>Review your details before proceeding to PayPal.</p>

              {/* Summary */}
              <div style={{ background: '#f9fafb', borderRadius: 10, padding: '16px 20px', marginBottom: 20, border: '1px solid #e5e7eb' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ color: '#6B7280', fontSize: 13 }}>Name</span>
                  <span style={{ color: '#111827', fontSize: 13, fontWeight: 600 }}>{form.firstName} {form.lastName}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ color: '#6B7280', fontSize: 13 }}>Company</span>
                  <span style={{ color: '#111827', fontSize: 13 }}>{form.companyName}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ color: '#6B7280', fontSize: 13 }}>Email</span>
                  <span style={{ color: '#111827', fontSize: 13 }}>{form.email}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ color: '#6B7280', fontSize: 13 }}>Location</span>
                  <span style={{ color: '#111827', fontSize: 13 }}>{form.city}{form.state ? `, ${form.state}` : ''}, {form.country}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ color: '#6B7280', fontSize: 13 }}>Plan</span>
                  <span style={{ color: '#FF7F32', fontSize: 13, fontWeight: 600 }}>{selectedPlan?.planName}</span>
                </div>
                {includeAddon && addonAvailable && (
  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
    <span style={{ color: '#6B7280', fontSize: 13 }}>Revision Add-on</span>
    <span style={{ color: '#FF7F32', fontSize: 13 }}>+${Number(selectedPlan?.addonPrice).toFixed(0)}</span>
  </div>
)}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ color: '#6B7280', fontSize: 13 }}>Plan Price</span>
                  <span style={{ color: '#111827', fontSize: 13 }}>${selectedPlan?.price}</span>
                </div>

                <div style={{ borderTop: '2px solid #FF7F32', marginTop: 12, paddingTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
  <span style={{ color: '#111827', fontWeight: 700, fontSize: 15 }}>Total Due Today</span>
  <span style={{ color: '#FF7F32', fontWeight: 800, fontSize: 26 }}>${total}<span style={{ color: '#9CA3AF', fontSize: 13, fontWeight: 400 }}>/{selectedPlan?.periodDays <= 7 ? 'week' : 'month'}</span></span>
</div>
              </div>

              {error && (
                <p style={{ color: '#f87171', fontSize: 13, background: 'rgba(239,68,68,0.1)', padding: '10px 14px', borderRadius: 8, marginBottom: 16 }}>{error}</p>
              )}

              <button
                onClick={handlePayPal}
                disabled={loading}
                style={{ width: '100%', background: loading ? '#666' : '#0070ba', color: '#fff', fontWeight: 700, fontSize: 15, padding: '14px', borderRadius: 10, border: 'none', cursor: loading ? 'not-allowed' : 'pointer', marginBottom: 12 }}>
                {loading ? 'Redirecting to PayPal...' : '🔒 Pay with PayPal'}
              </button>

              <button
                onClick={() => setStep('details')}
                style={{ width: '100%', background: 'transparent', color: '#6B7280', fontSize: 13, padding: '10px', border: '1px solid #e5e7eb', borderRadius: 10, cursor: 'pointer' }}>
                ← Back to Details
              </button>

              <p style={{ color: '#6B7280', fontSize: 11, textAlign: 'center', marginTop: 16 }}>
                🔒 Secured by PayPal. Your payment info is never stored on our servers.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  )
}